import type {
  createSuperAdminUserBody,
  createTeamMemberUserBody,
} from "@nlc/shared-types";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  superAdminSchema,
  teamMemberSchema,
  USER_TYPE_ENUM,
  UserRoleEnum,
} from "@nlc/shared-types";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/button";
import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import { FieldError, Label, LabeledGroup } from "@/components/field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { Text } from "@/components/text";

import { useResolveDesignations } from "../../hooks/useResolveDesignations";
import { useCreateTeamMember } from "../hooks/useCreateUser";
import { useCreateUserResolveTeams } from "../hooks/useCreateUserResolveTeams";
import { BasicInformation } from "./BasicInformation";
import { CreatingUserOverlay } from "./CreatingUserOverlay";

const ROLE_TO_USER_TYPE: Partial<Record<UserRoleEnum, USER_TYPE_ENUM>> = {
  [UserRoleEnum.CustomerSuccess]: USER_TYPE_ENUM.CUSTOMER_SUCCESS,
  [UserRoleEnum.Designer]: USER_TYPE_ENUM.DESIGNER,
  [UserRoleEnum.SuperAdmin]: USER_TYPE_ENUM.SUPER_ADMIN,
  [UserRoleEnum.TeamLeader]: USER_TYPE_ENUM.TEAM_LEADER,
};

type CreateTeamMemberProps = {
  roleId: number;
};

type CreateTeamMemberFormValues = {
  designationId: null | number;
  email: string;
  name: string;
  roleId: number;
  teamId: null | number;
};

export default function CreateTeamMember({ roleId }: CreateTeamMemberProps) {
  const navigate = useNavigate();
  const isSuperAdmin = roleId === UserRoleEnum.SuperAdmin;
  const userType =
    ROLE_TO_USER_TYPE[roleId as UserRoleEnum] ?? USER_TYPE_ENUM.DESIGNER;
  const { isPending, mutate } = useCreateTeamMember(userType);

  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
    setValue,
  } = useForm<CreateTeamMemberFormValues>({
    defaultValues: {
      designationId: isSuperAdmin ? null : 0,
      email: "",
      name: "",
      roleId,
      teamId: null,
    },
    mode: "onChange",
    resolver: zodResolver(isSuperAdmin ? superAdminSchema : teamMemberSchema),
  });

  const { data: designations, isLoading: loadingDesignations } =
    useResolveDesignations(isSuperAdmin ? undefined : roleId);
  const { data: teams, isLoading: loadingTeams } = useCreateUserResolveTeams();

  useEffect(() => {
    setValue("roleId", roleId);
    setValue("designationId", isSuperAdmin ? null : 0);
    setValue("teamId", null);
  }, [roleId, isSuperAdmin, setValue]);

  const isDesigner = roleId === UserRoleEnum.Designer;

  const onSubmit = (data: CreateTeamMemberFormValues) => {
    mutate(data as createSuperAdminUserBody | createTeamMemberUserBody, {
      onSuccess: () => {
        reset({
          designationId: isSuperAdmin ? null : 0,
          email: "",
          name: "",
          roleId,
          teamId: null,
        });
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CreatingUserOverlay isPending={isPending}>
        <BasicInformation
          control={control}
          emailField="email"
          nameField="name"
        />

        <div className="flex flex-col gap-y-lg mt-3 lg:grid lg:grid-cols-2 lg:gap-x-3xl">
          {!isSuperAdmin && (
            <Controller
              control={control}
              name="designationId"
              render={({ field, fieldState }) => (
                <LabeledGroup>
                  <Label requiredHint>Designation</Label>
                  <Select
                    isDisabled={loadingDesignations}
                    onSelectionChange={(key) => field.onChange(Number(key))}
                    placeholder={
                      loadingDesignations
                        ? "Loading..."
                        : "Select a designation"
                    }
                    selectedKey={field.value || null}
                  >
                    <SelectButton />
                    <SelectPopover>
                      <SelectListBox>
                        {designations?.map((d) => (
                          <SelectListItem
                            id={d.id}
                            key={d.id}
                            textValue={d.name}
                          >
                            {d.name}
                          </SelectListItem>
                        ))}
                      </SelectListBox>
                    </SelectPopover>
                  </Select>
                  {fieldState.error && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </LabeledGroup>
              )}
            />
          )}
          <Controller
            control={control}
            name="teamId"
            render={({ field }) => (
              <LabeledGroup className={isSuperAdmin ? "lg:col-span-2" : ""}>
                <Label>{isDesigner ? "Team" : "Team (optional)"}</Label>
                <ComboBox
                  allowsEmptyCollection
                  menuTrigger="input"
                  onSelectionChange={(key) =>
                    field.onChange(key ? Number(key) : null)
                  }
                  selectedKey={field.value ?? null}
                >
                  <ComboBoxGroup>
                    <ComboBoxInput
                      placeholder={
                        loadingTeams ? "Loading..." : "Select a team"
                      }
                    />
                    <ComboBoxButton />
                  </ComboBoxGroup>
                  <ComboBoxPopover>
                    <ComboBoxListBox
                      renderEmptyState={() => (
                        <Text className="p-2">No results found</Text>
                      )}
                    >
                      {teams?.map((t) => (
                        <ComboBoxListItem id={t.id} key={t.id}>
                          {t.name}
                        </ComboBoxListItem>
                      ))}
                    </ComboBoxListBox>
                  </ComboBoxPopover>
                </ComboBox>
              </LabeledGroup>
            )}
          />
        </div>

        <div className="flex justify-end mt-5 pt-xl border-t border-border-secondary">
          <Button
            className="mr-2"
            onPress={() => navigate({ to: "/users" })}
            type="button"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            isDisabled={!isValid || isPending}
            type="submit"
            variant="primary"
          >
            Create user
          </Button>
        </div>
      </CreatingUserOverlay>
    </form>
  );
}
